/**
 * Lesbare Darstellung der Protokoll-Einträge aus addressbook.audit_log (Jörg 2026-10-01:
 * „das Protokoll sagt nur, dass etwas gemacht wurde, aber nicht was genau. Das ist zu wenig").
 *
 * Jede Zeile bekommt einen Titel (was wurde gemacht) und Detailzeilen (was genau: Recht,
 * vorher → nachher, Level, Feld). Unbekannte Aktionen zeigen die Aktion und die Rohdaten
 * kompakt, damit nie Information verloren geht.
 */
import { getModuleLabel } from '@onvice/portal-ui';

export interface AuditLike {
  action: string;
  details: Record<string, unknown> | null | undefined;
}

export interface AuditText {
  title: string;
  lines: string[];
}

const LEVEL_TEXT: Record<string, string> = {
  none: 'kein Zugriff', depositor: 'Ablage', reader: 'Leser', tester: 'Tester', author: 'Autor', editor: 'Editor', admin: 'Admin',
};
const FIELD_TEXT: Record<string, string> = {
  first_name: 'Vorname', last_name: 'Nachname', firstName: 'Vorname', lastName: 'Nachname', email: 'E-Mail', role: 'Rolle',
  active: 'Aktiv', phone: 'Telefon', mobile: 'Mobil', hourly_rate: 'Stundensatz', hourlyRate: 'Stundensatz',
  weekly_hours: 'Wochenstunden', weeklyHours: 'Wochenstunden', hire_date: 'Eintrittsdatum', hireDate: 'Eintrittsdatum',
  street: 'Straße', zip: 'PLZ', city: 'Ort', birthday: 'Geburtstag', notes: 'Notizen', position: 'Position',
  department: 'Abteilung', emergency_contact: 'Notfallkontakt', emergencyContact: 'Notfallkontakt',
};
const SECURITY_TEXT: Record<string, string> = {
  'security.password_reset': 'Passwort zurückgesetzt',
  'security.reset_totp': 'Zwei-Faktor-Anmeldung zurückgesetzt',
  'security.lock': 'Konto gesperrt',
  'security.unlock': 'Konto entsperrt',
  'security.reset_failed_attempts': 'Fehlversuche zurückgesetzt',
  'security.terminate_session': 'Eine Sitzung beendet',
  'security.terminate_sessions': 'Alle Sitzungen beendet',
};

function level(v: unknown): string {
  const s = typeof v === 'string' ? v : 'none';
  return LEVEL_TEXT[s] ?? s;
}
function allowed(v: unknown): string {
  if (v === true) return 'erlaubt';
  if (v === false) return 'verboten';
  return 'Vorgabe des Levels';
}
function fieldName(key: string): string {
  return FIELD_TEXT[key] ?? key.replace(/_/g, ' ');
}
function show(v: unknown): string {
  if (v == null || v === '') return '–';
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
function moduleName(d: Record<string, unknown>): string {
  const slug = typeof d['module'] === 'string' ? d['module'] : typeof d['moduleSlug'] === 'string' ? d['moduleSlug'] : '';
  const name = typeof d['module_name'] === 'string' ? d['module_name'] : '';
  return name || (slug ? getModuleLabel(slug) : 'Modul');
}
function subjectText(d: Record<string, unknown>): string {
  const t = d['subject_type'];
  const name = typeof d['subject_name'] === 'string' ? d['subject_name'] : '';
  if (t === 'default') return 'Standard (alle ohne eigenen Eintrag)';
  if (t === 'group') return name ? `Gruppe „${name}"` : 'Gruppe';
  if (t === 'person') return name || 'Person';
  return '';
}

export function describeAudit(entry: AuditLike, fallbackLabels?: Record<string, string>): AuditText {
  const d = (entry.details ?? {}) as Record<string, unknown>;
  const a = entry.action;

  if (a === 'acl.module_permissions.update' || a === 'acl.module_permissions.preset') {
    const changes = Array.isArray(d['changes']) ? (d['changes'] as Array<Record<string, unknown>>) : [];
    // Ältere Einträge (vor dem 01.10.) tragen keine Beschriftungen — dann aus dem Katalog der Seite.
    const labels = { ...(fallbackLabels ?? {}), ...((d['labels'] ?? {}) as Record<string, string>) };
    const preset = typeof d['preset'] === 'string' ? d['preset'] : null;
    const who = subjectText(d);
    const title = a === 'acl.module_permissions.preset'
      ? `Rechte-Matrix ${moduleName(d)}: Preset „${level(preset)}" direkt angewendet${who ? ` für ${who}` : ''} (${changes.length} Rechte)`
      : `Rechte-Matrix ${moduleName(d)}${who ? ` für ${who}` : ''}: ${changes.length} ${changes.length === 1 ? 'Recht' : 'Rechte'} geändert${preset ? ` (vorbelegt aus Preset „${level(preset)}")` : ''}`;
    const lines = changes.map((c) => {
      const slug = typeof c['slug'] === 'string' ? c['slug'] : '?';
      return `${labels[slug] ?? slug}: ${allowed(c['from'])} → ${allowed(c['to'])}`;
    });
    return { title, lines };
  }

  if (a === 'acl.module_access.update') {
    const who = subjectText(d);
    return { title: `Modul-Zugriff ${moduleName(d)}${who ? ` für ${who}` : ''}: ${level(d['from'])} → ${level(d['to'])}`, lines: [] };
  }

  if (a === 'person.module_access_update') {
    const from = d['from'];
    return { title: `Modul-Zugriff ${moduleName(d)}: ${from === undefined ? '' : `${level(from)} → `}${level(d['level'])}`, lines: [] };
  }

  if (a === 'person.groups_update') {
    const ids = Array.isArray(d['groupIds']) ? (d['groupIds'] as unknown[]) : [];
    const names = Array.isArray(d['groupNames']) ? (d['groupNames'] as string[]) : [];
    return { title: `Gruppenzugehörigkeit gesetzt (${ids.length} ${ids.length === 1 ? 'Gruppe' : 'Gruppen'})`, lines: names.length ? [names.join(', ')] : [] };
  }

  if (a === 'person.update') {
    const changes = (d['changes'] ?? {}) as Record<string, unknown>;
    const before = (d['before'] ?? {}) as Record<string, unknown>;
    const sections = Array.isArray(d['sections']) ? (d['sections'] as string[]) : [];
    const lines: string[] = [];
    for (const [key, value] of Object.entries(changes)) {
      if (key === 'password') { lines.push('Passwort geändert'); continue; }
      const prev = before[key];
      if (prev !== undefined && show(prev) === show(value)) continue;
      lines.push(prev === undefined ? `${fieldName(key)}: ${show(value)}` : `${fieldName(key)}: ${show(prev)} → ${show(value)}`);
    }
    return { title: `Stammdaten geändert${sections.length ? ` (${sections.join(', ')})` : ''}`, lines };
  }

  if (a === 'field_access.update') {
    return { title: `Feldrecht „${show(d['slug'])}": ${level(d['level'])} für ${show(d['subjectType'])}`, lines: [] };
  }
  if (a === 'person.create') {
    return { title: 'Person angelegt', lines: [`${show(d['firstName'])} ${show(d['lastName'])}, ${show(d['email'])}, Rolle ${show(d['role'])}`] };
  }
  if (a === 'person.delete') return { title: 'Person gelöscht', lines: [] };
  if (a === 'report.edit_after_verify') return { title: 'Bericht nach der Verifizierung geändert', lines: [] };
  if (SECURITY_TEXT[a]) return { title: SECURITY_TEXT[a], lines: [] };

  // Unbekannt: Aktion + Rohdaten kompakt, nichts geht verloren.
  const raw = Object.entries(d).filter(([k]) => k !== 'userId').map(([k, v]) => `${k}: ${show(v)}`);
  return { title: a, lines: raw };
}

/** Tabellenzelle: Titel plus eingerückte Detailzeilen. */
export function AuditActionCell({ entry, labels }: { entry: AuditLike; labels?: Record<string, string> }) {
  const t = describeAudit(entry, labels);
  return (
    <div className="min-w-0" data-audit-action={entry.action}>
      <div className="text-slate-200">{t.title}</div>
      {t.lines.length > 0 && (
        <ul className="mt-0.5 space-y-0.5 text-[11px] text-slate-400">
          {t.lines.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
    </div>
  );
}
